/**
 * 通用文件预览组件
 * 支持Modal弹窗和内联两种模式
 * 支持图片、PDF、视频、Markdown等多种文件格式的预览
 */

import React from 'react';
import { Modal, Image } from 'antd';
import PDFViewer from './PDFViewer';
import MarkdownFetcher from './MarkdownFetcher';
import MarkdownRenderer from './MarkdownRenderer';

const FilePreviewModal = ({
  visible,
  onClose,
  file,
  width = '80%',
  height = 'calc(100vh - 300px)',
  top = 20,
  // 新增：内联模式支持
  inline = false,
  className = '',
  optimizeMode = false
}) => {
  if (!file) return null;

  const { content_type, filename, url, thumbUrl, title } = file;
  // 判断文件类型（优先使用content_type字段进行准确判断）
  const isImage = content_type === 'image';
  const isVideo = content_type === 'video';
  const isPDF = content_type === 'pdf';
  const isOffice = (content_type?.indexOf('presentation') !== -1) ||
                   (content_type?.indexOf('wordprocessing') !== -1) ||
                   url?.toLowerCase().indexOf('.docx') !== -1 ||
                   url?.toLowerCase().indexOf('.ppt') !== -1 ||
                   url?.toLowerCase().indexOf('.pptx') !== -1;
  const isMarkdown = content_type?.indexOf('markdown') !== -1;

  // 获取Modal标题 - 修复名称显示问题
  const getModalTitle = () => {
    // 优先使用title，然后是filename，最后是默认名称
    const displayName = title || filename || '未命名文件';
    
    if (isImage) return `图片预览 - ${displayName}`;
    if (isVideo) return `视频预览 - ${displayName}`;
    if (isPDF) return `PDF预览 - ${displayName}`;
    if (isMarkdown) return `Markdown预览 - ${displayName}`;
    return `文件预览 - ${displayName}`;
  };

  // 获取Modal宽度
  const getModalWidth = () => {
    if (isPDF) return '90%';
    if (isVideo) return '80%';
    if (isMarkdown) return '80%';
    if (isImage) return 'auto';
    return width;
  };

  // 渲染图片预览
  const renderImagePreview = () => (
    <div className={inline ? "w-full h-96 flex items-center justify-center bg-gray-100 rounded-xl" : "w-full h-full flex items-center justify-center bg-gray-100"}>
      <img
        src={url}
        alt={title || filename}
        className={inline ? "max-w-full max-h-full object-contain rounded-xl" : "max-w-full max-h-full object-contain"}
      />
    </div>
  );

  // 渲染视频预览
  const renderVideoPreview = () => (
    <div className={inline ? "w-full h-96 rounded-xl bg-black overflow-hidden relative" : "w-full h-full bg-black flex items-center justify-center"}>
      <video
        src={url}
        controls
        controlsList="nodownload"
        className="w-full h-full object-contain"
        preload="metadata"
        poster={thumbUrl || ''}
      />
    </div>
  );

  // 渲染PDF预览
  const renderPDFPreview = () => (
    <div className={inline ? "w-full min-h-96 bg-gray-50 rounded-xl shadow-sm border border-gray-200 overflow-hidden flex items-center justify-center relative mx-auto" : "w-full h-full bg-gray-50 rounded-xl shadow-sm border border-gray-200 overflow-hidden flex items-center justify-center"}>
      <PDFViewer
        url={url}
        title={title || filename}
        onClose={onClose}
        optimizeMode={optimizeMode}
      />
    </div>
  );

  // 渲染Office文档预览
  const renderOfficePreview = () => {
    const officeUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
    return (
      <div className={inline ? "w-full min-h-96 bg-gray-50 rounded-xl shadow-sm border border-gray-200 overflow-hidden flex items-center justify-center relative mx-auto" : "w-full h-full bg-gray-50 rounded-xl shadow-sm border border-gray-200 overflow-hidden flex items-center justify-center"}>
        <iframe
          src={officeUrl}
          title={title || filename}
          className={inline ? "w-full h-96 border-none bg-gray-50" : "w-full h-full border-none bg-gray-50"}
          loading="lazy"
          allowFullScreen
        />
      </div>
    );
  };

  // 渲染Markdown预览 - 智能判断URL是否为Markdown内容
  const renderMarkdownPreview = () => {

    
    // 否则使用原来的MarkdownFetcher逻辑
    return (
      <div className={inline ? "w-full min-h-96 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" : "w-full h-full bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"}>
        <div className={inline ? "p-6 max-h-96 overflow-y-auto" : "p-6 h-full overflow-y-auto"}>
          <MarkdownFetcher
            url={url}
            file={file}
          />
        </div>
      </div>
    );
  };

  // 渲染不支持的文件类型
  const renderUnsupportedPreview = () => (
    <div className={inline ? "w-full h-96 rounded-xl bg-gray-50 border border-gray-200 flex items-center justify-center" : "w-full h-full flex items-center justify-center"}>
      <div className="text-center">
        <div className="text-6xl text-gray-300 mb-4">📄</div>
        <div className="text-lg font-medium text-gray-600 mb-2">
          {title || filename || '未命名文件'}
        </div>
        <div className="text-sm text-gray-500 mb-4">
          该文件类型暂不支持在线预览
        </div>
        {!inline && (
          <div className="text-xs text-gray-400 space-y-1">
            <div>文件类型：{content_type}</div>
            <div>文件大小：{(file.size / 1024 / 1024).toFixed(2)} MB</div>
            <div>创建时间：{new Date(file.inserted_at).toLocaleString()}</div>
          </div>
        )}
      </div>
    </div>
  );

  // 渲染预览内容
  const renderPreviewContent = () => {
    if (isImage) {
      return renderImagePreview();
    }
    if (isVideo) {
      return renderVideoPreview();
    }
    if (isPDF) {
      return renderPDFPreview();
    }
    if (isOffice) {
      return renderOfficePreview();
    }
    if (isMarkdown) {
      return renderMarkdownPreview();
    }
    return renderUnsupportedPreview();
  };

  // 内联模式：直接返回预览内容
  if (inline) {
    return (
      <div className={className}>
        {renderPreviewContent()}
      </div>
    );
  }

  // Modal模式：返回Modal包装的预览内容
  return (
    <>
      <Modal
        title={getModalTitle()}
        open={visible}
        onCancel={onClose}
        footer={null}
        width={getModalWidth()}
        style={{ top }}
        bodyStyle={{ 
          height,
          padding: isPDF || isVideo ? 0 : 24,
          overflow: 'hidden'
        }}
        className="file-preview-modal"
      >
        {renderPreviewContent()}
      </Modal>
    </>
  );
};

export default FilePreviewModal;
